import { useLayoutEffect } from 'react'

/**
 * Detects iOS devices and adds the 'is-ios' class to the body element.
 * This enables iOS-specific CSS rules (e.g., overscroll-behavior: none)
 * to prevent bounce scrolling and pull-to-refresh on iOS Safari.
 *
 * Only targets iOS (WebKit), NOT Android (Chromium) which has a bug
 * with overscroll-behavior causing scroll issues.
 *
 * Should be called once at app initialization.
 * See: theme.less, BZ-4504
 */
export const initIOSBodyClass = (): void => {
  if (typeof document !== 'undefined' && isIOS()) {
    document.body.classList.add('is-ios')
  }
}

// https://stackoverflow.com/a/51733026/2108612
export const useIOSHorizScrollingFix = (elem: HTMLDivElement | undefined | null) => {
  useLayoutEffect(() => {
    if (!elem) {
      return
    }
    let lastX = 0
    const touchStartEvent = (e: TouchEvent) => {
      lastX = e.touches[0].clientX
    }
    const touchMoveEvent = (e: TouchEvent) => {
      const left = e.touches[0].clientX
      const currentTarget = e.currentTarget as HTMLDivElement
      if (currentTarget) {
        const scrollLeft = currentTarget.scrollLeft
        const maxScrollLeft = currentTarget.scrollWidth - currentTarget.offsetWidth
        const direction = lastX - left < 0 ? 'left' : 'right'

        if (
          e.cancelable &&
          ((direction === 'left' && scrollLeft <= 0) || (direction === 'right' && scrollLeft >= maxScrollLeft))
        ) {
          e.preventDefault()
        }
        lastX = left
      }
    }

    elem.addEventListener('touchstart', touchStartEvent)
    elem.addEventListener('touchmove', touchMoveEvent)
    return () => {
      elem?.removeEventListener('touchstart', touchStartEvent)
      elem?.removeEventListener('touchmove', touchMoveEvent)
    }
  }, [elem])
}

export const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}
